import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Send, Loader2, Save, TrendingUp, Lightbulb, Target, MessageSquare, Database } from "lucide-react";
import WidgetRenderer from "@/components/WidgetRenderer";

const STARTERS_BY_KIND = {
  employee_master: [
    "What does our workforce look like today?",
    "Show me headcount by department and band.",
    "How is gender representation across leadership levels?",
    "Where do we have the largest teams?",
  ],
  attrition: [
    "What is our attrition rate this year?",
    "Which departments have the highest attrition?",
    "Why are people leaving? Show top exit reasons.",
    "Compare voluntary vs involuntary attrition.",
  ],
  compensation: [
    "Show me average compensation by band.",
    "Are there any compensation outliers I should look at?",
    "Compare pay across departments — where is it uneven?",
    "What is our total compensation spend?",
  ],
  generic: [
    "Give me a workforce overview.",
    "What are the biggest patterns in this data?",
    "Show me the top 5 insights I should discuss with the CHRO.",
    "What has changed in our workforce this quarter?",
  ],
};

export default function AskPeopleIQ() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [datasets, setDatasets] = useState([]);
  const [activeDs, setActiveDs] = useState("auto");
  const [messages, setMessages] = useState([]); // [{role, content, response?}]
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [lastResponse, setLastResponse] = useState(null);
  const bottomRef = useRef();

  useEffect(() => {
    api.get("/datasets").then(({ data }) => setDatasets(data.datasets || []));
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, loading]);

  const ask = async (question) => {
    if (!question.trim()) return;
    if (datasets.length === 0) return toast.error("Upload data first to start asking questions");
    setLoading(true);
    setQ("");
    const userMsg = { role: "user", content: question };
    setMessages((p) => [...p, userMsg]);
    try {
      const conv = [...messages, userMsg].map((m) => ({ role: m.role, content: m.content }));
      const payload = { question, conversation: conv };
      if (activeDs !== "auto") payload.dataset_id = activeDs;
      const { data } = await api.post("/ai/ask", payload);
      setMessages((p) => [...p, { role: "assistant", content: data.answer, response: data }]);
      setLastResponse(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d : "AI error";
      toast.error(msg);
      setMessages((p) => [...p, { role: "assistant", content: `Sorry — ${msg}`, error: true }]);
    } finally { setLoading(false); }
  };

  const saveAsDashboard = async () => {
    if (!lastResponse?.dataset?.id || !lastResponse?.widgets?.length) return;
    const title = window.prompt("Dashboard title:", messages.filter(m => m.role === "user").slice(-1)[0]?.content?.slice(0, 60) || "AI Dashboard");
    if (!title) return;
    try {
      const { data } = await api.post("/dashboards", {
        dataset_id: lastResponse.dataset.id,
        title,
        description: lastResponse.insight || "",
        widgets: lastResponse.widgets,
      });
      toast.success("Dashboard saved");
      nav(`/t/${slug}/dashboards/${data.id}`);
    } catch { toast.error("Save failed"); }
  };

  // Determine starter suggestions based on datasets
  const activeKind = activeDs === "auto" ? datasets[0]?.kind : datasets.find(d => d.id === activeDs)?.kind;
  const starters = STARTERS_BY_KIND[activeKind] || STARTERS_BY_KIND.generic;

  const empty = messages.length === 0 && !loading;

  return (
    <div className="max-w-6xl mx-auto p-6 lg:p-10 pb-32">
      {/* Header */}
      <div className="mb-8">
        <div className="chip chip-primary mb-3"><Sparkles size={12} /> Ask PeopleIQ · Claude Sonnet 5</div>
        <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight text-slate-900">
          Ask your people data anything
        </h1>
        <p className="text-slate-500 mt-3 max-w-2xl">
          Natural-language questions become dashboards, insights, and recommendations — as if you had your own People Analytics team.
        </p>
      </div>

      {/* Dataset selector */}
      {datasets.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="dataset-scope">
          <span className="label-caps mr-2">Scope</span>
          <button onClick={() => setActiveDs("auto")} className={`chip ${activeDs === "auto" ? "chip-primary" : ""}`} data-testid="scope-auto">
            <Database size={11} /> Auto
          </button>
          {datasets.map((d) => (
            <button key={d.id} onClick={() => setActiveDs(d.id)} className={`chip ${activeDs === d.id ? "chip-primary" : ""}`} data-testid={`scope-${d.id}`}>
              {d.name}
            </button>
          ))}
        </div>
      )}

      {empty && datasets.length === 0 && (
        <div className="card text-center py-16" data-testid="no-data">
          <Database size={32} className="mx-auto text-slate-400 mb-4" />
          <div className="text-lg font-medium mb-2 text-slate-900">No data yet</div>
          <div className="text-slate-500 text-sm mb-6">Upload files or load sample data to start asking questions.</div>
          <button onClick={() => nav(`/t/${slug}/onboarding`)} className="btn-primary">Go to onboarding</button>
        </div>
      )}

      {empty && datasets.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
          {starters.map((s, i) => (
            <button key={i} data-testid={`starter-${i}`} onClick={() => ask(s)} className="card text-left hover:border-[var(--primary)] group">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-[var(--primary-soft)] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <MessageSquare size={14} className="text-[var(--primary)]" />
                </div>
                <span className="text-sm text-slate-700 group-hover:text-slate-900">{s}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Messages */}
      <div className="space-y-8">
        {messages.map((m, i) => (
          <div key={i} className="fade-up">
            {m.role === "user" ? (
              <div className="flex justify-end">
                <div className="max-w-[80%] bg-slate-900 text-white px-4 py-2.5 rounded-2xl rounded-br-md text-[15px]">
                  {m.content}
                </div>
              </div>
            ) : m.error ? (
              <div className="chip chip-danger">{m.content}</div>
            ) : (
              <AssistantMessage r={m.response} onSave={saveAsDashboard} onAsk={ask} />
            )}
          </div>
        ))}
        {loading && (
          <div className="flex items-center gap-3 text-slate-500 fade-up">
            <div className="w-8 h-8 rounded-full bg-[var(--primary-soft)] flex items-center justify-center">
              <Loader2 size={14} className="animate-spin text-[var(--primary)]" />
            </div>
            <span>PeopleIQ is analyzing<span className="blink-cursor" /></span>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Composer */}
      <div className="fixed bottom-6 left-0 right-0 lg:left-64 px-6 pointer-events-none z-30">
        <form onSubmit={(e) => { e.preventDefault(); ask(q); }} className="max-w-3xl mx-auto pointer-events-auto">
          <div className="bg-white rounded-2xl border border-[var(--border)] shadow-lg flex items-center gap-2 pl-4 pr-2 py-2">
            <Sparkles size={16} className="text-[var(--primary)] flex-shrink-0" />
            <input
              data-testid="ai-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Ask about attrition, engagement, headcount, comp…"
              disabled={loading}
              className="flex-1 bg-transparent outline-none border-0 text-slate-900 py-2 placeholder:text-slate-400"
            />
            <button type="submit" disabled={loading || !q.trim()} data-testid="ai-submit" className="btn-primary py-2 px-3">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AssistantMessage({ r, onSave, onAsk }) {
  if (!r) return null;
  return (
    <div className="space-y-5">
      {/* Answer */}
      <div className="flex gap-3">
        <div className="w-8 h-8 rounded-full bg-slate-900 flex items-center justify-center flex-shrink-0">
          <Sparkles size={14} className="text-white" />
        </div>
        <div className="flex-1 min-w-0 space-y-4">
          <div className="prose prose-slate max-w-none">
            <p className="text-[15px] leading-relaxed text-slate-800 m-0">{r.answer}</p>
          </div>

          {/* Insight & Recommendation */}
          {(r.insight || r.recommendation) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {r.insight && (
                <div className="card bg-blue-50/50 border-blue-100">
                  <div className="flex items-center gap-2 mb-2">
                    <Lightbulb size={13} className="text-[var(--primary)]" />
                    <span className="label-caps" style={{ color: "#1D4ED8" }}>Insight</span>
                  </div>
                  <p className="text-sm text-slate-800 leading-relaxed">{r.insight}</p>
                </div>
              )}
              {r.recommendation && (
                <div className="card bg-amber-50/50 border-amber-100">
                  <div className="flex items-center gap-2 mb-2">
                    <Target size={13} className="text-amber-600" />
                    <span className="label-caps" style={{ color: "#92400E" }}>Recommendation</span>
                  </div>
                  <p className="text-sm text-slate-800 leading-relaxed">{r.recommendation}</p>
                </div>
              )}
            </div>
          )}

          {/* Widgets */}
          {r.rendered_widgets?.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <TrendingUp size={13} className="text-slate-500" />
                  <span className="label-caps">Analysis · {r.dataset?.name}</span>
                </div>
                <button onClick={onSave} className="btn-soft" data-testid="save-as-dashboard">
                  <Save size={12} /> Save as Dashboard
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {r.rendered_widgets.map((w, i) => {
                  const isBig = w.config?.type === "table";
                  return (
                    <div key={w.id || i} className={isBig ? "md:col-span-2 xl:col-span-3" : ""}>
                      <WidgetRenderer widget={w} height={220} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Follow-ups */}
          {r.followup_questions?.length > 0 && (
            <div>
              <div className="label-caps mb-2">Follow-up questions</div>
              <div className="flex flex-wrap gap-2">
                {r.followup_questions.map((f, i) => (
                  <button key={i} onClick={() => onAsk(f)} data-testid={`followup-${i}`} className="chip hover:border-[var(--primary)] hover:text-[var(--primary)]">
                    {f}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
